<template>
    <div>
        <b-modal
            :visible="value"
            :id="modal_id"
            size="lg"
            @cancel="cancel"
            @close="cancel"
            @hidden="hidden"
            @shown="focusSearch"
        >
            <template #modal-title>
                Create Printable
            </template>

            <div class="row align-items-center">
                <div class="col-lg-12 order-lg-1 order-2">
                    <div class="mb-3">
                        Printable PDF
                        <b-form-file
                            v-model="submit_data.file"
                            placeholder="Choose a file or drop it here..."
                            drop-placeholder="Drop file here..."
                        ></b-form-file>
                    </div>

                    <b-form-group
                        label="Title"
                        label-for="title"
                    >
                        <b-form-input
                            type="text"
                            v-model="submit_data.title"
                        />
                        <b-form-invalid-feedback :state="!submit_data.title ? false : null">
                            Title is required
                        </b-form-invalid-feedback>
                    </b-form-group>

                    <b-form-group
                        label="Language"
                        label-for="language"
                    >
                        <language-multiselect
                            v-model="submit_data.language"
                            :options="languages"
                            @add="addLanguage"
                            @remove="removeLanguage"
                        />
                        <b-form-invalid-feedback :state="!submit_data.language ? false : null">
                            language is required
                        </b-form-invalid-feedback>
                    </b-form-group>

                    <b-form-group
                    >
                        <label>
                            Merlin ID(s)
                            <sup>
                                <font-awesome-icon
                                    :icon="['fad', 'info-circle']"
                                    content="For multiple Merlin ids, use comma separated values"
                                    class="text-secondary" v-tippy="{placement: 'top'}"
                                />
                            </sup>
                        </label>
                        <b-form-input
                            v-model="submit_data.merlin_ids"
                        />
                    </b-form-group>

                    <b-form-group
                        label="Tags"
                        label-for="tags"
                    >
                        <tag-multiselect
                            v-model="submit_data.tags"
                            :options="tags"
                        />
                    </b-form-group>

                    <b-form-group
                        label="Student Directions"
                        label-for="student_directions"
                    >
                        <b-form-textarea v-model="submit_data.student_directions" rows="2" max-rows="6" />
                    </b-form-group>

                    <b-form-group
                        label="Printable Text"
                        label-for="worksheet_text"
                    >
                        <b-form-textarea v-model="submit_data.worksheet_text" rows="2" max-rows="6" />
                    </b-form-group>

                    <b-form-group
                        label="Printable Suggestions"
                        label-for="worksheet_suggestions"
                    >
                        <b-form-textarea v-model="submit_data.worksheet_suggestions" rows="2" max-rows="6" />
                    </b-form-group>

                    <b-form-group
                        label="Artist Description"
                        label-for="artist_description"
                    >
                        <b-form-textarea v-model="submit_data.artist_description" rows="2" max-rows="6" />
                    </b-form-group>

                </div>
            </div>

            <template #modal-footer>
                <div v-if="!uploading_printable">
                    <b-button
                        variant="primary"
                        @click="uploadPrintableFile"
                        :disabled="validating()"
                    >
                        Create Printable
                    </b-button>
                </div>
                <div v-else>
                    <b-spinner small label="Uploading..."></b-spinner>
                    Uploading File - This may take a few minutes
                </div>
                <b-button
                    variant="light"
                    @click="cancel"
                    :disabled="uploading_printable"
                >
                    Cancel
                </b-button>
            </template>
        </b-modal>
    </div>
</template>

<style lang="scss" scoped></style>

<script>
import TagMultiselect from '@/components/TagMultiselect'
import {mapState} from 'vuex'

import LanguageMultiselect from '@/components/LanguageMultiselect'

import languages from '@/common/languages'
import moment from 'moment'

export default {
    data: () => ({
        submit_data: {
            title: '',
            description: '',
            caption: '',
            credit: '',
            tags: [],
            language: '',
            splash_file: null,
            file: null,
            url: null,
            alternate_media_id: null,
        },
        selected_type: null,
        languages: languages,
        uploading_printable: false,
        alternate_media_options: [],
    }),
    props: [
        'value',
    ],
    components: {
        TagMultiselect,
        LanguageMultiselect,
    },
    computed: {
        ...mapState({
            tags: state => state.tags.tags,
        }),
        modal_id() {
            let modal_id = 'add-related-media-modal'
            return modal_id
        }
    },
    watch: {
        value: {
            handler(value) {
                this.resetForm();
            }
        }
    },
    methods: {
        resetForm() {
            this.submit_data = {
                title: '',
                description: '',
                caption: '',
                credit: '',
                tags: [],
                language: '',
                splash_file: null,
                file: null,
                url: null,
                alternate_media_id: null,
            };
        },
        validating() {
            const {title, language} = this.submit_data;
            return !(title && language);
        },
        addLanguage(new_language) {
            this.submit_data.language = new_language;
        },
        removeLanguage() {
            this.submit_data.language = {};
        },
        addTags() {
            for (let tag of this.submit_data.tags) {
                this.axios.post('/api/media/add_printable_tag/' + this.submit_data.id, {
                    tag_id: tag.id
                })
            }
        },
        focusSearch() {
            if (
                this.$refs['related-media-search'] &&
                this.$refs['related-media-search'].$refs['search-term']
            ) {
                this.$refs['related-media-search'].$refs['search-term'].focus()
            }
        },
        cancel(event) {
            event.preventDefault()
            this.$emit('closeUpload');
            this.hide()
        },
        hide() {
            this.$emit('hideUploadPrintable')
        },
        hidden() {
            this.$emit('hideUploadPrintable')
        },
        async uploadPrintableFile() {
            const form_data = new FormData()
            form_data.append('file', this.submit_data.file)
            form_data.append('title', this.submit_data.title)

            if (this.submit_data.student_directions) {
                form_data.append('student_directions', this.submit_data.student_directions)
            }
            if (this.submit_data.worksheet_text) {
                form_data.append('worksheet_text', this.submit_data.worksheet_text)
            }
            if (this.submit_data.worksheet_suggestions) {
                form_data.append('worksheet_suggestions', this.submit_data.worksheet_suggestions)
            }
            if (this.submit_data.artist_description) {
                form_data.append('artist_description', this.submit_data.artist_description)
            }
            if (this.submit_data.language.language) {
                form_data.append('language', this.submit_data.language.language)
            }

            // format comma separated merlin_ids input into an array, only accept numbers
            const merlin_id_array = (this.submit_data.merlin_ids ? this.submit_data.merlin_ids : '')
                .split(',')
                .map((id) => parseInt(id.trim(), 10))
                .filter((id) => !isNaN(id))
            if (merlin_id_array.length > 0) {
                form_data.append('merlin_ids', merlin_id_array)
            }

            try {
                this.uploading_printable = true;
                const response = await this.axios.post('/api/media/upload_printable/', form_data)

                this.$bvToast.toast('Printable was successfully uploaded', {
                    title: 'Success',
                    variant: 'success',
                    solid: false,
                    autoHideDelay: 3000,
                    appendToast: true
                })
                if (response.data.worksheet_id) {
                    this.submit_data.id = response.data.worksheet_id
                    this.submit_data.cdn_url = response.data.media.value
                    this.submit_data.type = response.data.media.type
                    this.submit_data.language = response.data.media.language
                    if (this.submit_data.tags.length > 0) {
                        this.addTags()
                    }
                }
                this.$emit('link', this.submit_data)
                this.$emit('hideUploadPrintable')
                this.$emit('closeUpload')
                this.$emit('getPrintables')
                this.hide();
            } catch (e) {
                await this.$store.dispatch('pushError', {
                    data: {
                        message: `Failed to upload printable: ${e.message}`,
                    },
                    time: moment().toISOString(),
                })
            } finally {
                this.uploading_printable = false;

            }
        },
    },
}

</script>
